/**
 * @module components/hero
 *
 * The opening act. Three things happen here:
 *
 *  1. The ADM wheel scene is injected and given ambient, looping motion —
 *     ring draw-on, phase arcs lighting up, the AI swarm drifting, the core
 *     pulse.
 *  2. The headline and supporting copy run through a choreographed entrance
 *     timeline.
 *  3. A scrubbed parallax and a pointer follow give the scene depth as the user
 *     arrives and then scrolls away.
 *
 * All motion is created inside `gsap.matchMedia()` blocks, so reduced-motion
 * users get the same content, fully visible and completely static.
 */

import {
  gsap,
  EASE,
  DURATION,
  MEDIA,
  createMotionContext,
  splitText,
  ScrollTrigger,
} from "../core/gsap-setup.js";
import { qs, qsa } from "../core/dom.js";
import { HERO, HERO_MARKERS } from "../data/site.js";
import { admWheel } from "../svg/scenes.js";

/**
 * Apply the hero copy from the content module.
 *
 * The HTML ships the same copy so that search engines and no-JS readers see it
 * immediately; this function makes the data module authoritative at runtime, and
 * silently keeps the markup if the data is ever missing.
 *
 * @param {HTMLElement} title
 * @param {HTMLElement} lede
 */
function applyCopy(title, lede) {
  if (Array.isArray(HERO.titleLines) && HERO.titleLines.length) {
    title.innerHTML = HERO.titleLines
      .map(
        (line, index) =>
          `<span class="hero__title-line${
            index === HERO.accentLine ? " hero__title-line--accent" : ""
          }">${line}</span>`
      )
      .join("");
  }
  if (HERO.lede) lede.innerHTML = HERO.lede;
}

/**
 * @returns {() => void} Teardown.
 */
export function initHero() {
  const root = qs("[data-hero]");
  if (!root) return () => {};

  /* ---------------------------------------------------------------------- */
  /* Content + scene injection                                               */
  /* ---------------------------------------------------------------------- */

  const title = qs("[data-hero-title]", root);
  const lede = qs("[data-hero-lede]", root);
  if (title && lede) applyCopy(title, lede);

  const scene = qs("[data-hero-scene]", root);
  if (scene) {
    scene.innerHTML = admWheel({ accent: "#5fd4c4", secondary: "#d8a657", tertiary: "#a98bc4" });
  }

  const markerList = qs("[data-hero-markers]", root);
  if (markerList && !markerList.children.length) {
    markerList.innerHTML = HERO_MARKERS.map((marker) => `<li>${marker}</li>`).join("");
  }

  const copy = {
    eyebrow: qs("[data-hero-eyebrow]", root),
    title,
    lede,
    actions: qs("[data-hero-actions]", root),
    meta: qs("[data-hero-meta]", root),
    markers: markerList,
    cue: qs("[data-scroll-cue]", root),
  };

  const mm = createMotionContext();
  const splitters = [];
  let pointerDispose = () => {};

  /* ---------------------------------------------------------------------- */
  /* Reduced motion: everything visible, nothing moving                      */
  /* ---------------------------------------------------------------------- */

  mm.add(MEDIA.reduce, () => {
    const targets = Object.values(copy).filter(Boolean);
    gsap.set(targets, { clearProps: "all", autoAlpha: 1 });
    if (scene) gsap.set(scene, { clearProps: "all" });
  });

  /* ---------------------------------------------------------------------- */
  /* Full motion                                                             */
  /* ---------------------------------------------------------------------- */

  mm.add(MEDIA.any, () => {
    const scope = scene || root;

    /* --- Scene handles ---------------------------------------------------- */

    const ticks = qs(".wheel-ticks", scope);
    const rings = qsa(".wheel-orbit", scope);
    const arcs = qsa(".wheel-arc", scope);
    const codes = qsa(".wheel-code", scope);
    const sweep = qs(".wheel-sweep", scope);
    const swarm = qs(".wheel-swarm", scope);
    const dots = qsa(".wheel-dot", scope);
    const hub = qs(".wheel-hub", scope);
    const core = qs(".wheel-core", scope);

    /* --- Pre-animation state --------------------------------------------- */

    const copyTargets = [copy.eyebrow, copy.lede, copy.actions, copy.meta, copy.markers, copy.cue].filter(
      Boolean
    );
    gsap.set(copyTargets, { autoAlpha: 0, y: 26 });

    if (ticks) gsap.set(ticks, { autoAlpha: 0, scale: 0.92, transformOrigin: "50% 50%" });
    if (core) gsap.set(core, { autoAlpha: 0, scale: 0.72, transformOrigin: "50% 50%" });
    if (hub) gsap.set(hub, { autoAlpha: 0, scale: 0.86, transformOrigin: "50% 50%" });
    if (rings.length) gsap.set(rings, { drawSVG: "0%" });
    if (arcs.length) gsap.set(arcs, { drawSVG: "0%", autoAlpha: 0.2 });
    if (codes.length) gsap.set(codes, { autoAlpha: 0, scale: 0.7, transformOrigin: "50% 50%" });
    if (dots.length) gsap.set(dots, { autoAlpha: 0, scale: 0 });

    /* --- Entrance timeline ------------------------------------------------ */

    const split = copy.title ? splitText(copy.title, { type: "lines", linesClass: "split-line" }) : null;
    if (split) splitters.push(split);
    const lines = split?.lines ?? [];

    // The split re-creates the DOM, so the start state is set afterwards.
    if (lines.length) gsap.set(lines, { autoAlpha: 0, yPercent: 110 });

    const tl = gsap.timeline({
      defaults: { duration: DURATION.base, ease: EASE.out },
      delay: 0.12,
    });

    tl.addLabel("scene", 0)
      .to(ticks, { autoAlpha: 0.5, scale: 1, duration: 1.5 }, "scene")
      .to(rings, { drawSVG: "100%", duration: 1.7, ease: "power2.inOut", stagger: 0.13 }, "scene+=0.05")
      .to(arcs, { drawSVG: "100%", autoAlpha: 1, duration: 1.3, ease: "power2.inOut", stagger: 0.07 }, "scene+=0.3")
      .to(codes, { autoAlpha: 0.95, scale: 1, duration: 0.6, stagger: 0.05 }, "<+=0.35")
      .to(hub, { autoAlpha: 1, scale: 1, duration: 1.1, ease: "power2.out" }, "scene+=0.5")
      .to(
        dots,
        {
          autoAlpha: (i) => 0.3 + ((i * 41) % 55) / 100,
          scale: 1,
          duration: 1.1,
          ease: EASE.out,
          stagger: { each: 0.016, from: "random" },
        },
        "scene+=0.45"
      )
      .to(core, { autoAlpha: 0.85, scale: 1, duration: 1.5, ease: "power2.out" }, "scene+=0.6")
      .to(copy.eyebrow, { autoAlpha: 1, y: 0 }, "scene+=0.4")
      .to(lines, { autoAlpha: 1, yPercent: 0, duration: 1, ease: "power3.out", stagger: 0.08 }, "scene+=0.5")
      .to(copy.lede, { autoAlpha: 1, y: 0 }, "-=0.6")
      .to([copy.actions, copy.meta, copy.markers], { autoAlpha: 1, y: 0, stagger: 0.09 }, "-=0.45")
      .to(copy.cue, { autoAlpha: 1, y: 0, duration: 0.8 }, "-=0.3");

    /* --- Continuous ambient loops ----------------------------------------- */

    const loops = [
      sweep && gsap.to(sweep, { rotation: 360, duration: 26, ease: EASE.linear, repeat: -1 }),
      swarm && gsap.to(swarm, { rotation: -360, duration: 84, ease: EASE.linear, repeat: -1 }),
      hub && gsap.to(hub, { rotation: 360, duration: 120, ease: EASE.linear, repeat: -1 }),
      ticks && gsap.to(ticks, { rotation: 360, duration: 220, ease: EASE.linear, repeat: -1 }),
      core &&
        gsap.to(core, {
          scale: 1.06,
          duration: 3.4,
          ease: EASE.float,
          repeat: -1,
          yoyo: true,
          delay: 2.2,
        }),
    ].filter(Boolean);

    /* --- Scrubbed parallax ------------------------------------------------ */

    let parallax = null;
    if (scene) {
      parallax = gsap.to(scene, {
        yPercent: 20,
        scale: 1.1,
        autoAlpha: 0.35,
        ease: EASE.linear,
        scrollTrigger: { trigger: root, start: "top top", end: "bottom top", scrub: 0.6 },
      });
    }

    /* --- Pointer parallax ------------------------------------------------- */

    const hasFinePointer = window.matchMedia("(pointer: fine)").matches;
    if (hasFinePointer && scene) {
      const xTo = gsap.quickTo(scene, "x", { duration: 1.2, ease: "power3.out" });
      const yTo = gsap.quickTo(scene, "y", { duration: 1.2, ease: "power3.out" });

      const onMove = (event) => {
        const { innerWidth, innerHeight } = window;
        xTo((event.clientX / innerWidth - 0.5) * -44);
        yTo((event.clientY / innerHeight - 0.5) * -28);
      };

      window.addEventListener("pointermove", onMove, { passive: true });
      pointerDispose = () => window.removeEventListener("pointermove", onMove);
    }

    return () => {
      tl.kill();
      loops.forEach((loop) => loop.kill());
      parallax?.scrollTrigger?.kill();
      parallax?.kill();
      pointerDispose();
      pointerDispose = () => {};
    };
  });

  return () => {
    pointerDispose();
    splitters.forEach((split) => split.revert?.());
    mm.revert();
    ScrollTrigger.refresh();
  };
}
